import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { projectApi, won } from "./LaonaProjects";

/* =========================================================
   라오나봇 이용 규칙 — 무료로 시작하고, API 사용 비용이 무료 한도(plan.free_krw)를 넘으면 고른다:
   · 이용료(plan.fee) 결제 → 라오나봇과 이어서 대화 · 설계한다 (계좌이체, 관리자가 SSTOA 에서 입금 확인)
   · 직접 만들기로 넘어가기 → 지금 설계의 연결 리스트로 수동 프로젝트를 만든다. 연결도 · 브레드보드 배선 · 부품 주문까지,
     펌웨어는 스스로 빌드한다.
   펌웨어 개발 · 빌드는 이용료와 따로, 내 프로젝트에서 주문할 때 부품과 함께 낸다(기본 5만원).
   (백엔드 app/laona_projects.py 의 /pay · /manual)
   ========================================================= */

/* 무료 한도는 회원마다 하나 — 다른 미결제 프로젝트에 쓴 비용(others_krw) + 이 프로젝트 사용량으로 본다 */
export function planUsed(p, usage) {
  return (p.plan?.others_krw || 0) + (usage?.krw ?? p.usage?.krw ?? 0);
}

export function planOver(p, usage) {
  const plan = p.plan || {};
  return !p.admin && !plan.paid && planUsed(p, usage) >= (plan.free_krw ?? 1000);
}

export default function PlanPanel({ p, plan, bank, reason, onChanged }) {
  const navigate = useNavigate();
  const [depositor, setDepositor] = useState(p.depositor || p.member?.name || "");
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [note, setNote] = useState("");
  const requested = plan.pay_requested;

  /* 입금 확인을 기다리는 동안 — 확인되면 이 안내가 저절로 사라진다 (30초마다 확인) */
  useEffect(() => {
    if (!requested) return undefined;
    const t = setInterval(() => { if (document.visibilityState === "visible") onChanged(); }, 30000);
    return () => clearInterval(t);
  }, [requested, onChanged]);

  async function pay(e) {
    e.preventDefault();
    if (!depositor.trim()) { setMsg("입금자명을 적어 주세요."); return; }
    setBusy("pay");
    setMsg("");
    setNote("");
    try {
      await projectApi(`/${p.id}/pay`, { method: "POST", body: JSON.stringify({ depositor: depositor.trim() }) });
      setNote(requested ? "입금자명을 바꿨습니다." : "");
      await onChanged();
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy("");
    }
  }

  async function toManual() {
    if (!window.confirm("결제하지 않고 직접 만들기로 넘어갈까요?\n지금 설계(연결 리스트)를 담은 수동 프로젝트가 만들어지고, "
      + "이 프로젝트의 라오나봇 대화는 닫힙니다."
      + (requested ? "\n\n결제 요청도 거둡니다. 이미 입금하셨다면 넘어가기 전에 문의해 주세요." : ""))) return;
    setBusy("manual");
    setMsg("");
    try {
      const d = await projectApi(`/${p.id}/manual`, { method: "POST" });
      navigate(`/laonabot/wiring?manual=${d.manual_id}`);
    } catch (err) {
      setMsg(err.message);
      setBusy("");
    }
  }

  return (
    <div className="lb-deposit lb-deposit--win lb-plan">
      <h2>{reason || `무료 사용 한도 ${won(plan.free_krw)}를 다 썼습니다`}</h2>
      <p>이어서 진행할 방법을 골라 주세요.</p>

      <section className="lb-plan__opt">
        <b>이용료 {won(plan.fee)} 결제하고 이어서 진행</b>
        <p>라오나봇과 계속 대화 · 설계합니다. 설계를 마치고 내 프로젝트로 저장하면 부품과 펌웨어 개발 · 빌드(기본 5만원)를 함께 주문합니다.</p>
        {bank && (
          <div className="lb-deposit__bank">
            <span>계좌이체</span>
            <b>{bank.bank} {bank.account}</b>
            <span>예금주 {bank.holder}</span>
            <span>금액 <b>{won(plan.fee)}</b></span>
          </div>
        )}
        <form className="lb-deposit__who" onSubmit={pay}>
          <label htmlFor="planDepositor">입금자명</label>
          <div>
            <input id="planDepositor" value={depositor} maxLength={60} onChange={(e) => setDepositor(e.target.value)} />
            <button type="submit" className={`lb-btn lb-btn--sm${requested ? "" : " lb-btn--primary"}`}
                    disabled={!!busy || !depositor.trim() || (requested && depositor.trim() === p.depositor)}>
              {busy === "pay" ? "보내는 중…" : requested ? "입금자명 바꾸기" : "결제 요청"}
            </button>
          </div>
          <span className="lb-deposit__hint">
            {requested
              ? "입금을 기다리고 있습니다. 확인되면 이 안내가 사라지고 메일로도 알려 드립니다."
              : "결제를 요청한 뒤 위 계좌로 입금해 주세요. 통장에 찍히는 이름과 같아야 빨리 확인됩니다."}
          </span>
        </form>
        {requested && <button type="button" className="lb-btn lb-btn--sm" onClick={onChanged}>입금 확인됐는지 다시 보기</button>}
      </section>

      <section className="lb-plan__opt">
        <b>결제 없이 직접 만들기로 넘어가기</b>
        <p>
          지금까지의 설계를 연결도 도구로 옮겨, 연결 작업 · 브레드보드 배선 · 부품 주문까지 직접 이어갑니다.
          펌웨어는 Arduino IDE 나 PlatformIO 로 스스로 작성 · 빌드해 보드에 올립니다.
          {requested && " 결제 요청을 해 두었다면 그 요청은 거둡니다."}
        </p>
        <button type="button" className="lb-btn lb-btn--sm" disabled={!!busy} onClick={toManual}>
          {busy === "manual" ? "옮기는 중…" : "직접 만들기로 넘어가기"}
        </button>
      </section>
      {note && <p className="lb-mk-note" role="status">{note}</p>}
      {msg && <p className="lb-error">{msg}</p>}
    </div>
  );
}
